import { bool, string } from 'prop-types';
import React, { useState } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Button from 'yoda-core-components/lib/components/Button';
import CMImageRenderer from '../CMImageRenderer/CMImageRenderer';
import Carousel from '../Carousel';
import { metaDataElement, metaDataProperty } from '../../utils/cmPreviewMetaData';
import {
    commonAutomationIds,
    ctaAutomationIds,
    bannerAutomationIds,
} from '../CoreMediaPage/CMPage.config';
import { cmsCompHeadlineStyle, cmsCompSubHeadlineStyle } from '../../helpers/CMSUtils/cmsStyles';
import RevieveModal from '../Revieve/RevieveModal';

const renderImages = (data, isCMPreview) => {
    const [imageHeight, setImageHeight] = useState(0);
    const imagesDom = [];

    const viznavContainerStyle = dt(['self-start', 'mb-4', 'pr-2']);
    const viznavStackedContainerStyle = dt(['lg:w-2/12', 'md:w-2/12', 'sm:w-4/12']);
    data?.items?.forEach((item, i) => {
        const analyticsId = get(item, 'analyticsId', '');
        const imageToRender = (
            <CMImageRenderer
                imageURL={item?.media[0]?.uriTemplate || ''}
                altText={item?.media[0]?.alt || ''}
                componentType={data?.componentType || 'viz-nav-stacked'}
                imageAspectRatio="portrait_ratio1x1"
                itemPictureShape={data?.styles?.itemStyles?.itemPictureShape || 'square'}
                isCMPreview={isCMPreview}
                data={data}
                setImageHeight={
                    data.componentType === 'viz-nav-carousel' && i === 0 ? setImageHeight : null
                }
                automationId={bannerAutomationIds?.IMAGE}
            />
        );
        const banner = (
            <div
                className={[
                    data.componentType !== 'viz-nav-carousel' ? viznavStackedContainerStyle : null,
                    viznavContainerStyle,
                ].join(' ')}
                data-automation-id={bannerAutomationIds?.BANNER}
            >
                <a
                    role="link"
                    aria-label="viznav-teaser"
                    data-automation-id={bannerAutomationIds?.LINK}
                    href={item?.urlHolder?.value}
                    target={item?.urlHolder?.openInNewTab ? '_blank' : ''}
                    title={item?.altText || ''}
                    rel="noreferrer"
                >
                    <RevieveModal
                        data={analyticsId}
                        isCoremedia
                        componentToBeRendered={imageToRender}
                    />
                    {item?.text ? (
                        <div
                            className={dt([
                                'md:text-sm',
                                'font-normal',
                                'md:leading-[1.43]',
                                'text-center',
                                'text-black',
                                'smOnly:text-normal',
                                'smOnly:pt-2',
                                'md:pt-4',
                                data.componentType !== 'viz-nav-carousel' ? 'sm:!pt-2' : '',
                            ])}
                            style={{ color: `#${data?.styles?.itemStyles?.itemTextColor}` }}
                            data-automation-id={bannerAutomationIds?.CTATEXT}
                        >
                            {item?.text}
                        </div>
                    ) : null}
                </a>
            </div>
        );
        imagesDom.push(banner);
    });
    if (data.componentType === 'viz-nav-carousel') {
        const isValidHexColor = /^#([0-9a-f]{3}){1,2}$/i;
        const isBGWhite =
            !isValidHexColor.test(`#${data?.styles?.componentContainerBackgroundColor}`) ||
            isEmpty(data?.styles?.componentContainerBackgroundColor) ||
            data?.styles?.componentContainerBackgroundColor === '#' ||
            data?.styles?.componentContainerBackgroundColor === '#None' ||
            data?.styles?.componentContainerBackgroundColor === 'ffffff' ||
            data?.styles?.componentContainerBackgroundColor === 'fff' ||
            data?.styles?.componentContainerBackgroundColor === 'rgb(255, 255, 255)' ||
            data?.styles?.componentContainerBackgroundColor === 'rgba(255, 255, 255, 1)';
        const paddingVizNavCarouselStyle = isBGWhite
            ? dt(['lg:-ml-1', 'smOnly:!w-auto'])
            : dt(['md:px-6']);
        const wrapperStyles = isBGWhite
            ? dt(['smOnly:-mr-6', 'smOnly:!w-auto', 'smOnly:-ml-6'])
            : dt(['smOnly:!w-auto', 'smOnly:-mx-6']);
        return (
            <Carousel
                theme="viz-nav-carousel"
                children={imagesDom}
                automationId={bannerAutomationIds?.CAROUSEL}
                wrapperClass={wrapperStyles}
                contentWrapperClass={paddingVizNavCarouselStyle}
                carouselImageHeight={imageHeight}
                sliderItemContainerStyle={dt(['smOnly:pl-6', 'smOnly:scroll-pl-6'])}
            />
        );
    }
    return imagesDom;
};

const CMVizNav = (props) => {
    const data = get(props, 'data', props);
    const automationId = get(data, 'automationId', '');
    const ctaStyle = data?.styles?.componentCtaStyle || 'link';
    const { isCMPreview } = props;
    // Styles
    const vizNavHeadFont = cmsCompHeadlineStyle[data?.styles?.componentHeadlineFontSize] || '';
    const vizNavSubHeadFont =
        cmsCompSubHeadlineStyle[data?.styles?.componentSubHeadlineFontSize] || '';
    const isBackgroundWhite =
        data?.styles?.componentContainerBackgroundColor === 'ffffff' ||
        data?.styles?.componentContainerBackgroundColor === 'fff' ||
        data?.styles?.componentContainerBackgroundColor === 'rgb(255, 255, 255)' ||
        data?.styles?.componentContainerBackgroundColor === 'rgba(255, 255, 255, 1)';

    const imagesStyles = [
        'flex',
        'flex-wrap',
        'justify-center',
        'self-center',
        data.componentType !== 'viz-nav-carousel' ? 'gap-y-6' : '',
        data.componentType !== 'viz-nav-carousel' && isBackgroundWhite ? '-mx-1' : '',
    ];

    const vizNavCarouselSectionStyle = isBackgroundWhite
        ? ['smOnly:py-0', 'md:pt-4']
        : ['pt-4', 'smOnly:-mx-6', 'smOnly:px-6'];
    const vizNavStackedSectionStyle = isBackgroundWhite
        ? ['py-0']
        : ['md:py-6', 'smOnly:py-4', 'px-5', 'smOnly:-mx-6'];
    const vizNavSectionStyle =
        data.componentType === 'viz-nav-carousel'
            ? vizNavCarouselSectionStyle
            : vizNavStackedSectionStyle;

    const vizNavTitleSectionStyle = [
        'pb-6',
        data.componentType !== 'viz-nav-carousel' && !isBackgroundWhite ? 'pl-1' : '',
        data.componentType !== 'viz-nav-carousel' && !isBackgroundWhite ? 'pr-1' : '',
    ];

    return (
        <div
            id={data.componentType}
            className={dt([...vizNavSectionStyle])}
            style={{ backgroundColor: `#${data?.styles?.componentContainerBackgroundColor}` }}
            data-cm-metadata={isCMPreview && metaDataElement(data)}
            data-automation-id={automationId}
        >
            <div
                className={dt([
                    'flex',
                    'flex-col',
                    'text-center',
                    'self-start',
                    'justify-center',
                    'w-full',
                    ...vizNavTitleSectionStyle,
                ])}
            >
                {data?.teaserTitle ? (
                    <div
                        className={`${dt(['font-bold', 'text-black', 'block'])} ${vizNavHeadFont}`}
                        id="vizNavTitle"
                        style={{ color: `#${data?.styles?.componentHeadlineColor}` }}
                        data-cm-metadata={isCMPreview && metaDataProperty('teaserTitle')}
                        data-automation-id={commonAutomationIds?.MAINTITLE}
                    >
                        {data.teaserTitle}
                    </div>
                ) : null}
                {data?.teaserSubtitle ? (
                    <div
                        className={`${dt([
                            'font-normal',
                            'text-black',
                            'block',
                            // data?.cta?.ctaText ? 'pb-6' : '',
                        ])} ${vizNavSubHeadFont}`}
                        id="vizNavSubTitle"
                        style={{ color: `#${data?.styles?.componentSubHeadlineColor}` }}
                        data-cm-metadata={isCMPreview && metaDataProperty('teaserSubtitle')}
                        data-automation-id={commonAutomationIds?.MAINSUBTITLE}
                    >
                        {data.teaserSubtitle}
                    </div>
                ) : null}
                {data?.cta?.ctaText ? (
                    <div
                        className={
                            ctaStyle === 'link'
                                ? dt(['flex', 'items-center', 'justify-center', 'mt-6', 'lg:mt-4'])
                                : dt(['flex', 'items-center', 'justify-center', 'mt-6'])
                        }
                    >
                        <Button
                            automationId={ctaAutomationIds?.BUTTON}
                            linkUrl={data?.cta?.urlHolder ? data.cta.urlHolder?.value : ''}
                            id="vizNavComponentCTA"
                            buttonType={ctaStyle === 'link' ? 'Text' : 'Secondary'}
                            size="Lg"
                            target={data?.cta?.urlHolder?.openInNewTab ? '_blank' : ''}
                            title={data?.cta?.ctaAltText || ''}
                            type="button"
                            style={
                                ctaStyle === 'link'
                                    ? { color: `#${data?.styles?.componentCtaColor}` }
                                    : null
                            }
                            data-cm-metadata={isCMPreview && metaDataProperty('cta.ctaText')}
                        >
                            {data.cta.ctaText}
                        </Button>
                    </div>
                ) : null}
            </div>
            <div
                className={dt([...imagesStyles])}
                data-automation-id={bannerAutomationIds?.BANNERSSECTION}
            >
                {renderImages(data, isCMPreview)}
            </div>
        </div>
    );
};

CMVizNav.defaultProps = {
    componentType: '',
    isCMPreview: false,
};

CMVizNav.propTypes = {
    componentType: string,
    isCMPreview: bool,
};

export default CMVizNav;
